/**
 * sessionStorage for the short-lived forced-password-change token
 * (PCI DSS 8.3.5, #1198).
 *
 * Shared because admin and hub differ only in the storage key — and they used
 * to differ in behaviour too: admin read the entry raw and never looked at
 * `expires_at`, so an expired token was posted to the server and came back as
 * a confusing rejection instead of a clean "sign in again".
 *
 * A tampered payload cannot bypass anything: the `change_token` is
 * server-issued, single-use, rate-limited and re-validated on every call, and
 * the current password must be supplied alongside it. The shape + expiry check
 * here is purely so a stale or malformed entry surfaces as "start again"
 * rather than a white screen or a misleading error.
 */

/**
 * @typedef {Object} ForcedPasswordChangeEntry
 * @property {string} change_token
 * @property {number} expires_at Epoch milliseconds.
 */

/**
 * @param {string} key sessionStorage key (app-prefixed, e.g. `admin_forced_password_change`).
 * @returns {{
 *   read: () => (ForcedPasswordChangeEntry | { expired: true } | null),
 *   write: (payload: ForcedPasswordChangeEntry) => void,
 *   clear: () => void,
 * }}
 */
export function createForcedPasswordChangeStorage(key) {
  function available() {
    return typeof sessionStorage !== 'undefined'
  }

  /**
   * Returns the parked entry, `{ expired: true }` when it has aged out, or
   * `null` when there is nothing usable. Anything unusable is removed so the
   * next read starts clean.
   */
  function read() {
    if (!available()) return null

    const raw = sessionStorage.getItem(key)
    if (!raw) return null

    let parsed
    try {
      parsed = JSON.parse(raw)
    } catch (_err) {
      sessionStorage.removeItem(key)
      return null
    }

    const valid =
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.change_token === 'string' &&
      parsed.change_token.length > 0 &&
      typeof parsed.expires_at === 'number'

    if (!valid) {
      sessionStorage.removeItem(key)
      return null
    }

    if (Date.now() > parsed.expires_at) {
      sessionStorage.removeItem(key)
      return { expired: true }
    }

    return parsed
  }

  /** @param {ForcedPasswordChangeEntry} payload */
  function write(payload) {
    if (!available()) return
    sessionStorage.setItem(key, JSON.stringify(payload))
  }

  function clear() {
    if (!available()) return
    sessionStorage.removeItem(key)
  }

  return { read, write, clear }
}
